import React, { Dispatch, SetStateAction, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { DURATIONS, TOASTTYPE, useToast } from '@/ui/Toast/Toast';
import { Flex } from '@/ui/Flex/Flex';
import { FrontendSlide, AssetType, StoredSlide } from '@/types/SlideType';
import { IconLoading, IconTrash } from '@/icons';
import { Button } from '@/ui/Button/Button';
import { fetcher } from '@/utils/fetcher';
import { RETAIL_API_URL } from 'src/utils/envConfig';
import { useUserInfo } from '@/features/_data/useUserInfo';
import { useIntl } from 'react-intl';
import { Tooltip } from '@/ui/Tooltip/Tooltip';

const StyledAssetLibrary = styled(Flex)<{
  readonly isSelected: boolean;
  readonly isDeleted: boolean;
}>`
  width: 100%;
  height: 100%;
  cursor: pointer;
  position: relative;
  flex-direction: column;
  border-radius: 8px;
  align-self: center;
  justify-self: center;
  border: 3px solid rgba(200, 200, 200, 0.2);

  padding: 8px;

  ${({ isDeleted }) => css`
    display: ${isDeleted ? 'none' : 'flex'};
  `};
`;

const StyledAssetName = styled.input`
  border: solid 1px transparent;
  background: none;
  outline: none;
  padding: 2px 4px;
  width: 100%;

  font-weight: 600;
  font-size: 14px;
  line-height: 150%;
  color: ${({ theme }) => theme.color('#1B1A1F')};

  &:focus {
    border: solid 1px #ccc;
    border-radius: 4px;
  }
`;

const StyledLoadingWrapper = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-grow: 1;

  svg {
    width: 48px;
    height: 48px;
    stroke: ${({ theme }) => theme.color('#676570')};
  }
`;
const StyledSlideSelector = styled.div`
  display: grid;
  place-items: center;
`;

interface Props {
  isReplacing?: FrontendSlide | null;
  readonly setIsSlideLibraryOpen: (value: boolean) => void;
  readonly onChangeNameInLibrary: (slideId: string, newName: string) => void;
  readonly setVideos: React.Dispatch<React.SetStateAction<FrontendSlide[]>>;
  readonly setImages: React.Dispatch<React.SetStateAction<FrontendSlide[]>>;
  readonly setWebslides: React.Dispatch<React.SetStateAction<FrontendSlide[]>>;
  readonly disallowSlideDeletion: boolean;
  readonly onUpdateSlide: (
    slideId: string | null,
    slideData: Partial<FrontendSlide> | null,
    isFromLibrary?: boolean,
    slidePosition?: number,
  ) => void;
  slidePosition?: number;
  asset: FrontendSlide;
}

type StateType<T extends boolean> = T extends true
  ? HTMLImageElement
  : HTMLVideoElement;

const SlideLibraryAsset = ({
  isReplacing,
  setIsSlideLibraryOpen,
  onChangeNameInLibrary,
  asset,
  disallowSlideDeletion,
  onUpdateSlide,
  setVideos,
  setImages,
  setWebslides,
  slidePosition,
}: Props) => {
  const toast = useToast();
  const intl = useIntl();
  const { isAdmin } = useUserInfo();
  const [selectedAsset, setSelectedAsset] = useState<null | string>(null);
  const ref = useRef<StateType<boolean>>(null);
  const [loaded, setLoaded] = useState(false);
  const [deleted, setDeleted] = useState(false);
  const [assetName, setAssetName] = useState(asset.name);
  const actualAssetType = asset.type as AssetType;
  const storedAsset = asset as StoredSlide;

  const handleOnload = () => {
    setLoaded(true);
  };

  const handleDeleteAsset = async (id: string, type: AssetType) => {
    try {
      await fetcher(`${RETAIL_API_URL}/slides/delete?id=${id}`, {
        method: 'POST',
      });

      if (type === 'image') {
        setImages((prev) => {
          return prev.filter((img) => img.id !== id);
        });
      } else if (type === 'video') {
        setVideos((prev) => {
          return prev.filter((vid) => vid.id !== id);
        });
      } else if (type === 'web.url') {
        setWebslides((prev) => {
          return prev.filter((web) => web.id !== id);
        });
      }

      setDeleted(true);

      toast({
        subtitle: intl.formatMessage({
          defaultMessage: 'The slide has been deleted!',
          id: 'rU29vE',
        }),
        mode: TOASTTYPE.Success,
      });
    } catch (err) {
      return toast({
        title: intl.formatMessage({
          defaultMessage: `Slide is still in use.`,
          id: '7keaBB',
        }),
        subtitle: intl.formatMessage({
          defaultMessage:
            "Cannot delete slide while it's in use, delete it from all configs first.",
          id: 'rx2yYC',
        }),
        time: DURATIONS.Medium,
        mode: TOASTTYPE.Warn,
      });
    }
  };

  const handleUpdateSlideName = async (newName: string) => {
    try {
      const result = await fetcher(`${RETAIL_API_URL}/slides/update`, {
        method: 'POST',
        body: JSON.stringify({
          id: asset.id,
          name: newName,
        }),
      });

      if (!result) {
        throw new Error('Failed to update slide name');
      }

      // Update the appropriate state based on asset type
      const updateStateFn: Record<
        AssetType,
        Dispatch<SetStateAction<FrontendSlide[]>>
      > = {
        image: setImages,
        video: setVideos,
        'web.url': setWebslides,
      };

      if (updateStateFn && actualAssetType) {
        updateStateFn[actualAssetType]((prev: FrontendSlide[]) =>
          prev.map((item) =>
            item.id === asset.id ? { ...item, name: newName } : item,
          ),
        );
      }
      onChangeNameInLibrary(asset.id ?? '', assetName);
      toast({
        subtitle: intl.formatMessage({
          defaultMessage: 'Slide name updated successfully!',
          id: 'vCwIv3',
        }),
        mode: TOASTTYPE.Success,
      });
    } catch (error) {
      console.error('Error updating slide name:', error);
      toast({
        title: intl.formatMessage({
          defaultMessage: 'Failed to update slide name. Please try again.',
          id: 'z8HpHm',
        }),
        mode: TOASTTYPE.Danger,
      });
    }
  };

  return (
    <StyledAssetLibrary
      isDeleted={deleted}
      isSelected={selectedAsset === asset.asset}
    >
      <Flex justify="space-between" align="center">
        <Tooltip title="Click to edit name">
          <StyledAssetName
            title={asset.name}
            value={assetName}
            onChange={(e) => setAssetName(e.target.value)}
            onBlur={() => {
              if (asset.name === assetName) {
                return;
              }
              handleUpdateSlideName(assetName);
            }}
            onKeyDown={(event) =>
              event.key === 'Enter' && event.currentTarget.blur()
            }
          />
        </Tooltip>
        {((!isAdmin && !disallowSlideDeletion) || isAdmin) && (
          <Button
            mode="transparent"
            onClick={(e) => {
              if (asset.id && asset.type) {
                const actualDeleteType = asset.type as AssetType;
                e.stopPropagation();
                handleDeleteAsset(asset.id, actualDeleteType);
              }
            }}
          >
            <IconTrash />
          </Button>
        )}
      </Flex>
      {!loaded && asset.type !== 'web.url' && (
        <StyledLoadingWrapper>
          <IconLoading />
        </StyledLoadingWrapper>
      )}
      <StyledSlideSelector
        onClick={() => {
          setSelectedAsset(storedAsset.asset ?? null);

          if (isReplacing) {
            onUpdateSlide(isReplacing.slideId ?? null, null);
            onUpdateSlide(
              storedAsset.id ?? '',
              {
                asset: asset.asset,
                customer: asset.customer,
                name: asset.name,
                contentType: asset.contentType,
                size: asset.size,
                structureData: isReplacing.structureData,
              },
              true,
              slidePosition,
            );
          } else {
            onUpdateSlide(
              storedAsset.id ?? '',
              {
                asset: asset.asset,
                customer: asset.customer,
                name: asset.name,
                contentType: asset.contentType,
                size: asset.size,
              },
              true,
              slidePosition,
            );
          }

          setIsSlideLibraryOpen(false);
        }}
      >
        {asset.type === 'image' || asset.type === 'web.url' ? (
          <img
            alt="slide"
            ref={ref as React.RefObject<HTMLImageElement>}
            style={{
              borderRadius: '8px',
              margin: 'auto',
              objectFit: 'cover',
              maxHeight: '100px',
              maxWidth: '200px',
              opacity: loaded ? '1' : '0',
              transition: 'opacity 0.2s ease-in-out',
            }}
            src={asset.asset}
            loading="lazy"
            onLoad={handleOnload}
          />
        ) : (
          <video
            onCanPlay={handleOnload}
            src={asset.asset}
            ref={ref as React.RefObject<HTMLVideoElement>}
            autoPlay
            muted
            style={{
              borderRadius: '8px',
              margin: 'auto',
              objectFit: 'cover',
              maxHeight: '100px',
              maxWidth: '200px',
              opacity: loaded ? '1' : '0',
              transition: 'opacity 0.2s ease-in-out',
            }}
          />
        )}
      </StyledSlideSelector>
    </StyledAssetLibrary>
  );
};

export default SlideLibraryAsset;
